import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ClipboardList, TrendingUp, Calendar, AlertCircle, ChevronRight } from 'lucide-react';
import api from '../../api';
import DashboardLayout from '../../layouts/DashboardLayout';

const SupervisorReportees = () => {
    const navigate = useNavigate();
    const [reportees, setReportees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.getSupervisorReportees()
            .then((res) => setReportees(res.data))
            .catch(() => setError('Failed to load your reportees. Please try again.'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="p-8 text-center text-secondary-500 animate-pulse">Loading your team...</div>
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <div className="p-8 flex items-center gap-3 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-2xl font-heading font-bold text-secondary-900 flex items-center gap-2">
                        <Users className="w-6 h-6 text-primary-600" />
                        My Team
                    </h1>
                    <p className="text-secondary-500 mt-1">
                        Junior clinicians under your supervision — review their sessions and LLM evaluations.
                    </p>
                </div>

                {reportees.length === 0 ? (
                    <div className="bg-white rounded-xl border border-secondary-100 shadow-sm p-12 text-center">
                        <Users className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                        <h3 className="text-lg font-bold text-secondary-800">No reportees assigned yet</h3>
                        <p className="text-secondary-500 mt-1 text-sm">
                            Ask your admin or ops team to assign junior clinicians to you.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {reportees.map((rep) => (
                            <ReporteeCard
                                key={rep.reportee_id}
                                reportee={rep}
                                onViewSessions={() =>
                                    navigate(`/ConsultationHistory?doctor_id=${rep.reportee_id}`)
                                }
                                onViewDashboard={() =>
                                    navigate(`/therapist-dashboard?doctor_id=${rep.reportee_id}`)
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

const ReporteeCard = ({ reportee, onViewSessions, onViewDashboard }) => {
    const statusColor = {
        completed: 'text-green-600 bg-green-50',
        processing: 'text-amber-600 bg-amber-50',
        pending: 'text-secondary-500 bg-secondary-50',
    }[reportee.last_session_status] || 'text-secondary-400 bg-secondary-50';

    return (
        <div className="bg-white rounded-xl border border-secondary-100 shadow-sm hover:shadow-md transition-shadow p-6">
            {/* Avatar + Name */}
            <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-white flex items-center justify-center text-lg font-bold shadow-sm ring-2 ring-white">
                    {reportee.name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-secondary-900 truncate">{reportee.name}</p>
                    <p className="text-xs text-secondary-500 truncate">{reportee.email}</p>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-secondary-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-secondary-400 mb-1">
                        <ClipboardList className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium uppercase tracking-wide">Sessions</span>
                    </div>
                    <p className="text-2xl font-bold text-secondary-900">{reportee.session_count}</p>
                </div>

                <div className="bg-secondary-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-secondary-400 mb-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium uppercase tracking-wide">Last Session</span>
                    </div>
                    {reportee.last_session_date ? (
                        <>
                            <p className="text-sm font-bold text-secondary-900">{reportee.last_session_date}</p>
                            <span className={`mt-1 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${statusColor}`}>
                                {reportee.last_session_status || 'unknown'}
                            </span>
                        </>
                    ) : (
                        <p className="text-sm text-secondary-400 italic">No sessions yet</p>
                    )}
                </div>
            </div>

            {/* Assigned since */}
            <p className="text-xs text-secondary-400 mb-4">
                Assigned since {reportee.assigned_at?.split(' ')[0]}
            </p>

            {/* Actions */}
            <div className="flex gap-2">
                <button
                    onClick={onViewSessions}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 text-sm font-medium transition-colors"
                >
                    <ClipboardList className="w-4 h-4" />
                    Sessions
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <button
                    onClick={onViewDashboard}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-sm font-medium transition-colors"
                >
                    <TrendingUp className="w-4 h-4" />
                    Analytics
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
};

export default SupervisorReportees;
